import { HttpErrorResponse } from '@angular/common/http';
import { of, Subscription } from 'rxjs';
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import {
  AbstractFilterElement,
  Column,
  ColumnOption,
  CustomAction,
  CustomTableAction,
  DataTableComponent,
  NimmstaDataSource,
  EmptyTableComponent,
  SelectionFilterElement,
  ResultFieldMultiFilterElement,
  ResultFieldOption,
  FilterElementSize,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  AppDialogRef,
  AppDialogService,
} from '@nimmsta/ng-commons-lib/services/app-dialog';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import {
  DataConnection,
  Job,
  JobStatus,
  Pageable,
  Worker,
} from '@nimmsta/ng-commons-lib/models';
import { StatusBadgePipe, DateTimePipe } from '@nimmsta/ng-commons-lib/pipes';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { InputWithErrorComponent } from '@nimmsta/ng-commons-lib/forms';
import { TableConfigurationService } from '@nimmsta/ng-commons-lib/services/table-configuration';
import { ResultFieldsService } from '@nimmsta/ng-commons-lib/services/result-fields';
import { JobService } from '../services/job.service';
import { JobExportService } from '../services/job-export.service';
import { DataConnectionService } from '../services/dataConnection.service';
import { StatusDependentAction } from './statusDependentAction';
import { NamePipe } from './name.pipe';
import { WorkerService } from '@nimmsta/ng-b2b-lib/services/worker';
import { TranslatePipe } from '@ngx-translate/core';
import { DatePicker } from 'primeng/datepicker';
import { LaddaModule } from 'angular2-ladda';
import { WorkflowJobExportRequest } from '../models/jobExport';

_('job.cancel');
_('job.delete');

@Component({
  selector: 'app-job-table',
  templateUrl: 'job-table.component.html',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormsModule,
    DatePicker,
    LaddaModule,
    TranslatePipe,
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    InputWithErrorComponent,
  ],
  providers: [StatusBadgePipe, DateTimePipe, NamePipe],
})
export class JobTableComponent implements OnInit, OnDestroy {
  private authorizationService = inject(AuthorizationService);
  private jobService = inject(JobService);
  private router = inject(Router);
  private statusBadge = inject(StatusBadgePipe);
  private dateTimePipe = inject(DateTimePipe);
  private namePipe = inject(NamePipe);
  private modalService = inject(AppDialogService);
  private dataConnectionService = inject(DataConnectionService);
  private workerService = inject(WorkerService);
  private jobExportService = inject(JobExportService);
  private tableConfigService = inject(TableConfigurationService);
  private resultFieldsService = inject(ResultFieldsService);
  @Input()
  customActions: CustomAction<Job>[] = [];

  @Input()
  customTableActions: CustomTableAction<Job>[] = [];

  @Input()
  updatedTranslation = 'job.lastUpdatedAt';

  @Input()
  filters: string[] = [];

  @Input()
  showExport = false;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<Job>;

  connectionControl!: FormControl;

  jobSource!: NimmstaDataSource<Job>;

  error?: HttpErrorResponse;

  modalError?: HttpErrorResponse;

  private exportSubscription?: Subscription;

  modalRef?: AppDialogRef;

  dataConnections: DataConnection[] = [];

  filterElements: AbstractFilterElement[] = [];

  customFieldOptions: ColumnOption[] = [];

  baseColumns: Column[] = [];

  activeColumns: Column[] = [];

  private resultFieldFilterElement?: ResultFieldMultiFilterElement;

  private savedVisibleColumns: string[] | null = null;

  public get connectionValues(): string[] {
    return this.dataConnections?.map((d) => d.identifier);
  }

  public get connectionNames(): string[] {
    return this.dataConnections?.map((d) => d.name);
  }

  dateRange!: Date[];

  addWorker = true;

  complete = true;

  deleted = true;

  cancelled = true;

  loading = false;

  canWriteJobs = false;

  get columns(): Column[] {
    return this.activeColumns.length > 0
      ? this.activeColumns
      : this.baseColumns;
  }

  @ViewChild('exportModal', { static: true })
  exportModal!: TemplateRef<unknown>;

  constructor() {
    const today = new Date();
    const lastMonth = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      today.getDate(),
    );
    this.dateRange = [lastMonth, today];
  }

  ngOnInit(): void {
    this.canWriteJobs = this.authorizationService.hasPermission('job.write');
    this.baseColumns = [
      new Column('name', 'job.name', { sortable: true }),
      new Column('status', 'job.status', {
        pipe: this.statusBadge,
        sortable: true,
      }),
      new Column('dataConnection.name', 'dataconnection.singular', {
        sortable: true,
      }),
      new Column('createdAt', 'job.createdAt', {
        pipe: this.dateTimePipe,
        sortable: true,
      }),
      new Column('lastUpdatedAt', this.updatedTranslation, {
        pipe: this.dateTimePipe,
        sortable: true,
        invertSorting: true,
      }),
      new Column('lastWorker', 'job.lastWorker', {
        pipe: this.namePipe,
        sortable: false,
      }),
    ];

    this.dataConnectionService
      .list({
        filter: null,
        sortColumn: 'name',
        pageSize: 9999,
      })
      .subscribe((page: Pageable<DataConnection>) => {
        this.deferUpdate(() => {
          this.dataConnections = page.content;
          if (this.dataConnections.length === 1) {
            this.connectionControl = new FormControl(
              this.dataConnections[0].identifier,
              Validators.required,
            );
          } else {
            this.connectionControl = new FormControl(null, Validators.required);
          }
          if (this.showExport && this.dataConnections.length > 0) {
            const exportAction = new CustomTableAction('export', 'ios_share');
            this.customTableActions.push(exportAction);
            exportAction.subscribe(() => this.showExportModal());
          }
          const dataConnectionValues: Record<string, string> = {};
          this.dataConnections.forEach((dataConnection) => {
            dataConnectionValues[dataConnection.id] = dataConnection.name;
          });
          const dataConnectionFilter = new SelectionFilterElement(
            'dataconnection.singular',
            'dataConnection.id',
            dataConnectionValues,
            FilterElementSize.MEDIUM,
            false,
            true,
            false,
            false,
            () => of({}),
          );
          this.filterElements = [...this.filterElements, dataConnectionFilter];
        });
      });

    // Load workers for the worker filter
    this.workerService
      .list({
        filter: null,
        sortColumn: 'given_name',
        pageSize: 9999,
      })
      .subscribe((page: Pageable<Worker>) => {
        this.deferUpdate(() => {
          const workerValues: Record<string, string> = {};
          page.content.forEach((worker) => {
            const displayName =
              `${worker.given_name || ''} ${worker.family_name || ''}`.trim() ||
              worker.email;
            workerValues[worker.id] = displayName;
          });

          const workerFilter = new SelectionFilterElement(
            'job.lastWorker',
            'lastWorker.id',
            workerValues,
            FilterElementSize.MEDIUM,
            false,
            true,
            false,
            false,
            () => of({}),
          );
          this.filterElements = [...this.filterElements, workerFilter];
        });
      });

    this.jobSource = new NimmstaDataSource<Job>(this.jobService);
    this.filters.forEach((filter) => this.jobSource.defaultFilter.push(filter));
    if (this.canWriteJobs) {
      const cancelAction = new StatusDependentAction('job.cancel', [
        JobStatus.OPEN,
      ]);
      this.customActions.push(cancelAction);
      cancelAction.subscribe((id) => this.cancel(id));
      const deleteAction = new StatusDependentAction(
        'delete',
        [JobStatus.OPEN],
        true,
      );
      this.customActions.push(deleteAction);
      deleteAction.subscribe((id) => this.delete(id));
    }
    // Load column configuration
    this.loadColumnConfiguration();

    // Render the result filter immediately to avoid layout shift while options load
    this.initializeResultFieldFilter();

    // Load available result and data fields for column configuration
    this.loadResultFields();
    this.loadDataFields();
  }

  private initializeResultFieldFilter(): void {
    this.resultFieldFilterElement = new ResultFieldMultiFilterElement(
      'filter.result-field.label',
      'resultField',
      [],
      FilterElementSize.EXTRA_LARGE,
      false,
      'filter.result-field.select-field',
      'filter.result-field.enter-value',
    );
    this.filterElements.push(this.resultFieldFilterElement);
  }

  private loadColumnConfiguration(): void {
    this.tableConfigService.getConfiguration('jobs').subscribe((config) => {
      this.deferUpdate(() => {
        if (config?.visibleColumns) {
          this.savedVisibleColumns = config.visibleColumns;
          this.updateActiveColumns(config.visibleColumns);
        }
      });
    });
  }

  private loadResultFields(): void {
    this.resultFieldsService.discoverFields().subscribe((response) => {
      this.deferUpdate(() => {
        const resultOptions: ColumnOption[] = response.fields.map((field) => ({
          key: `result.${field.key}`,
          label: field.key,
          selected:
            this.savedVisibleColumns?.includes(`result.${field.key}`) ?? false,
          isCustom: true,
        }));
        this.customFieldOptions = [...resultOptions, ...this.dataFieldOptions];

        if (this.resultFieldFilterElement) {
          const resultFieldOptions: ResultFieldOption[] = response.fields.map(
            (field) => ({
              key: field.key,
              label: field.key,
            }),
          );
          this.resultFieldFilterElement.setFields(resultFieldOptions);
        }
      });
    });
  }

  private dataFieldOptions: ColumnOption[] = [];

  private loadDataFields(): void {
    this.resultFieldsService.discoverDataFields().subscribe((response) => {
      this.deferUpdate(() => {
        this.dataFieldOptions = response.fields.map((field) => ({
          key: `data.${field.key}`,
          label: field.key,
          selected:
            this.savedVisibleColumns?.includes(`data.${field.key}`) ?? false,
          isCustom: true,
        }));
        this.customFieldOptions = [
          ...this.customFieldOptions.filter((o) => !o.key.startsWith('data.')),
          ...this.dataFieldOptions,
        ];
      });
    });
  }

  private deferUpdate(update: () => void): void {
    setTimeout(() => {
      update();
    }, 0);
  }

  private updateActiveColumns(visibleColumns: string[]): void {
    const baseColumnMap = new Map(
      this.baseColumns.map((col) => [col.displayedProperty, col]),
    );

    // Deduplicate visible columns
    const uniqueVisibleColumns = [...new Set(visibleColumns)];

    this.activeColumns = uniqueVisibleColumns
      .map((key) => {
        // Check if it's a base column
        if (baseColumnMap.has(key)) {
          return baseColumnMap.get(key)!;
        }

        // It's a custom result or data column
        if (key.startsWith('result.')) {
          const fieldName = key.substring(7); // Remove 'result.' prefix
          return new Column(key, fieldName, {
            sortable: false,
            translateHeader: false,
          });
        }

        if (key.startsWith('data.')) {
          const fieldName = key.substring(5); // Remove 'data.' prefix
          return new Column(key, fieldName, {
            sortable: false,
            translateHeader: false,
          });
        }

        return null;
      })
      .filter((col): col is Column => col !== null);
  }

  onColumnConfigurationChanged(visibleColumns: string[]): void {
    this.savedVisibleColumns = visibleColumns;
    this.updateActiveColumns(visibleColumns);
  }

  ngOnDestroy(): void {
    this.exportSubscription?.unsubscribe();
  }

  show(id: string): void {
    this.router.navigate(['/jobs', id]);
  }

  delete(id: string): void {
    const job = this.getSelectedObject(id)!;
    this.jobService.delete(job).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  cancel(id: string): void {
    const job = this.getSelectedObject(id)!;
    this.jobService.cancel(job).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  getSelectedObject(id: string): Job | undefined {
    return this.jobSource.data?.content.filter((d) => id === d.id).pop();
  }

  emptyButtonClick(): void {
    this.router.navigate(['/dataconnection']);
  }

  showExportModal(): void {
    this.modalRef = this.modalService.open(this.exportModal);
  }

  export(): void {
    if (this.loading) {
      return;
    }

    const dataConnectionIdentifier = this.connectionControl.value as
      string | null;
    if (dataConnectionIdentifier === null) {
      this.connectionControl.markAsTouched();
      return;
    }
    const request: WorkflowJobExportRequest = {
      dataConnectionIdentifier,
      from: this.formatDate(this.dateRange[0]),
      to: this.formatDate(this.dateRange[1]),
      complete: this.complete,
      deleted: this.deleted,
      cancelled: this.cancelled,
      addLogistician: this.addWorker,
      mode: 'AUTO',
    };

    this.loading = true;
    this.modalError = undefined;
    this.exportSubscription?.unsubscribe();
    this.exportSubscription = this.jobExportService
      .startRegularExport(request)
      .subscribe({
        next: (response) => {
          if (response.status !== 'COMPLETED' || !response.downloadUrl) {
            this.modalError = this.createExportFailedError(
              response.errorMessage,
            );
            this.loading = false;
            return;
          }

          this.jobExportService.downloadExportFile(response.downloadUrl);
          this.loading = false;
          this.modalRef?.close();
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          if (error.status === 404) {
            const customError = { ...error };
            customError.error.error = 'error.custom.no-jobs-to-export';
            this.modalError = customError;
          } else {
            this.modalError = error;
          }
        },
      });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private createExportFailedError(
    errorMessage: string | null,
  ): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 500,
      statusText: 'Export failed',
      error: {
        error: errorMessage || 'error.InternalServerError',
      },
    });
  }
}
